import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Warehouse } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { STORE_TYPES, orgReturnTo, type Branch, type Store } from './org-types';

export function StorePage() {
  const { storeId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('stores'));

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get<Branch[]>('/org/branches'),
  });
  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => get<Store[]>('/pharmacy/stores'),
    enabled: Boolean(storeId),
  });
  const store = stores.data?.find((row) => row.id === storeId) ?? null;

  if (!branches.data || (storeId && !store)) {
    return (
      <FormScreen title={storeId ? tr('admin', 'loading') : tr('admin', 'add_a_store')} icon={Warehouse} backTo={returnTo}>
        <RecordState error={branches.error ?? stores.error} missing={Boolean(stores.data)} />
      </FormScreen>
    );
  }

  return <StoreForm key={storeId || 'new'} store={store} branches={branches.data} returnTo={returnTo} />;
}

function StoreForm({ store, branches, returnTo }: { store: Store | null; branches: Branch[]; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const lock = useBranchLock();
  const [initial] = useState(() => ({
    branchId: store?.branchId ?? (lock.locked ? lock.branchId : ''),
    code: store?.code ?? '',
    name: store?.name ?? '',
    type: store?.type ?? 'main_pharmacy',
    isSellingPoint: store?.isSellingPoint ?? true,
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        branchId: form.branchId,
        code: form.code.toUpperCase(),
        name: form.name,
        type: form.type,
        isSellingPoint: form.isSellingPoint,
        isActive: store?.isActive ?? true,
      };
      return store ? put(`/pharmacy/stores/${store.id}`, payload) : post('/pharmacy/stores', payload);
    },
    onSuccess: () => {
      toast.success(store ? tr('admin', 'store_updated') : tr('admin', 'store_created'));
      void queryClient.invalidateQueries({ queryKey: ['stores'] });
      void queryClient.invalidateQueries({ queryKey: ['pharmacy-stores'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.branchId) && form.code.length >= 2 && form.name.length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={store ? tr('admin', 'edit_x', { name: store.name }) : tr('admin', 'add_a_store')}
      description={store ? tr('admin', 'a_store_cannot_be_moved_to_another_branch_its') : tr('admin', 'stock_is_counted_per_store_so_create_one_per')}
      icon={Warehouse}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'store')} icon={Warehouse}>
        <Field label={tr('admin', 'branch')} required className="sm:col-span-2">
          <Select value={form.branchId} disabled={Boolean(store)} readOnly={lock.locked} onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
            <option value="">{tr('admin', 'select_a_branch')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('admin', 'code')} required>
          <Input value={form.code} maxLength={16} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={tr('admin', 'type')} required>
          <ChoiceButtons
            layout="chips"
            value={form.type}
            onChange={(type) => setForm({ ...form, type })}
            required
            options={STORE_TYPES.map((type) => ({ value: type, label: humanize(type) }))}
          />
        </Field>
        <Field label={tr('admin', 'name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          checked={form.isSellingPoint}
          onCheckedChange={(checked) => setForm({ ...form, isSellingPoint: checked })}
          label={tr('admin', 'dispenses_to_patients_appears_in_the_pharmacy')}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={store ? tr('admin', 'save_store') : tr('admin', 'create_store')}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
